
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarIcon, TrendingUpIcon, TrendingDownIcon, PiggyBankIcon, AlertTriangleIcon, BarChart3 } from "lucide-react";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Button } from "@/components/ui/button";

import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, ResponsiveContainer, XAxis, YAxis, Bar, CartesianGrid, Legend, Tooltip } from "recharts";
import { Transacao, ResumoFinanceiro } from "@/types/financeiro";

function calcularResumoFinanceiro(transacoes: Transacao[]): ResumoFinanceiro {
  const dataAtual = new Date();
  const primeiroDiaMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1);
  const ultimoDiaMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth() + 1, 0);

  // Filtrar transações do mês atual
  const transacoesMes = transacoes.filter(
    (t) => new Date(t.data) >= primeiroDiaMes && new Date(t.data) <= ultimoDiaMes
  );

  // Calcular saldos
  const receitasMes = transacoesMes
    .filter((t) => t.tipo === "receita" && t.status === "pago")
    .reduce((sum, t) => sum + t.valor, 0);

  const despesasMes = transacoesMes
    .filter((t) => t.tipo === "despesa" && t.status === "pago")
    .reduce((sum, t) => sum + t.valor, 0);

  const receitasPendentes = transacoesMes
    .filter((t) => t.tipo === "receita" && t.status === "pendente")
    .reduce((sum, t) => sum + t.valor, 0);

  const despesasPendentes = transacoesMes
    .filter((t) => t.tipo === "despesa" && t.status === "pendente")
    .reduce((sum, t) => sum + t.valor, 0);

  // Calcular todas as receitas e despesas (independente do mês)
  const todasReceitas = transacoes
    .filter((t) => t.tipo === "receita" && t.status === "pago")
    .reduce((sum, t) => sum + t.valor, 0);

  const todasDespesas = transacoes
    .filter((t) => t.tipo === "despesa" && t.status === "pago")
    .reduce((sum, t) => sum + t.valor, 0);

  return {
    saldoAtual: todasReceitas - todasDespesas,
    receitasMes,
    despesasMes,
    receitasPendentes,
    despesasPendentes,
  };
}

function prepararDadosGrafico(transacoes: Transacao[]) {
  const dataAtual = new Date();
  const meses: { [key: string]: { mes: string, receitas: number, despesas: number } } = {};

  // Pegar os últimos 6 meses
  for (let i = 5; i >= 0; i--) {
    const data = new Date(dataAtual.getFullYear(), dataAtual.getMonth() - i, 1);
    const mesAno = format(data, "MMM/yy", { locale: pt });
    meses[mesAno] = { mes: mesAno, receitas: 0, despesas: 0 };
  }

  // Filtrar e agrupar transações dos últimos 6 meses
  transacoes.forEach(t => {
    const data = new Date(t.data);
    // Verificar se está nos últimos 6 meses
    if (data >= new Date(dataAtual.getFullYear(), dataAtual.getMonth() - 5, 1)) {
      const mesAno = format(data, "MMM/yy", { locale: pt });
      if (meses[mesAno]) {
        if (t.status === "pago") {
          if (t.tipo === "receita") {
            meses[mesAno].receitas += t.valor;
          } else {
            meses[mesAno].despesas += t.valor;
          }
        }
      }
    }
  });

  return Object.values(meses);
}

interface DashboardFinanceiroProps {
  transacoes: Transacao[];
}

const DashboardFinanceiro: React.FC<DashboardFinanceiroProps> = ({ transacoes }) => {
  const resumo = calcularResumoFinanceiro(transacoes);
  const dadosGrafico = prepararDadosGrafico(transacoes);

  // Transações futuras (próximos 30 dias)
  const hoje = new Date();
  const em30Dias = new Date();
  em30Dias.setDate(hoje.getDate() + 30);

  const transacoesFuturas = transacoes
    .filter(t => {
      const dataTransacao = new Date(t.data);
      return dataTransacao >= hoje && dataTransacao <= em30Dias && t.status !== "cancelado";
    })
    .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());

  return (
    <div className="space-y-8">
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="financeiro-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-financeiro-text">Saldo Atual</CardTitle>
            <PiggyBankIcon className="h-4 w-4 text-financeiro-text-light" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${resumo.saldoAtual >= 0 ? 'text-financeiro-accent' : 'text-financeiro-text'}`}>
              {resumo.saldoAtual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-financeiro-text-light">
              Balanço entre receitas e despesas
            </p>
          </CardContent>
        </Card>
        
        <Card className="financeiro-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-financeiro-text">Receitas do Mês</CardTitle>
            <TrendingUpIcon className="h-4 w-4 text-financeiro-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-financeiro-accent">
              {resumo.receitasMes.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-financeiro-text-light">
              {resumo.receitasPendentes > 0 && (
                <span>+ {resumo.receitasPendentes.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} pendentes</span>
              )}
              {resumo.receitasPendentes === 0 && <span>Não há receitas pendentes</span>}
            </p>
          </CardContent>
        </Card>
        
        <Card className="financeiro-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-financeiro-text">Despesas do Mês</CardTitle>
            <TrendingDownIcon className="h-4 w-4 text-financeiro-text" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-financeiro-text">
              {resumo.despesasMes.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-financeiro-text-light">
              {resumo.despesasPendentes > 0 && (
                <span>+ {resumo.despesasPendentes.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} pendentes</span>
              )}
              {resumo.despesasPendentes === 0 && <span>Não há despesas pendentes</span>}
            </p>
          </CardContent>
        </Card>
        
        <Card className="financeiro-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-financeiro-text">Balanço do Mês</CardTitle>
            {resumo.receitasMes - resumo.despesasMes >= 0 ? (
              <TrendingUpIcon className="h-4 w-4 text-financeiro-accent" />
            ) : (
              <AlertTriangleIcon className="h-4 w-4 text-financeiro-text" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(resumo.receitasMes - resumo.despesasMes) >= 0 ? 'text-financeiro-accent' : 'text-financeiro-text'}`}>
              {(resumo.receitasMes - resumo.despesasMes).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-financeiro-text-light">
              {(resumo.receitasMes - resumo.despesasMes) >= 0 ? 'Resultado positivo' : 'Resultado negativo'}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="receitas-despesas" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="receitas-despesas">Receitas vs Despesas</TabsTrigger>
          <TabsTrigger value="proximas-transacoes">Próximas Transações</TabsTrigger>
        </TabsList>
        
        <TabsContent value="receitas-despesas" className="mt-6">
          <Card className="financeiro-card">
            <CardHeader>
              <CardTitle className="text-financeiro-text-dark">Evolução Financeira</CardTitle>
              <CardDescription className="text-financeiro-text-light">Acompanhamento dos últimos 6 meses</CardDescription>
            </CardHeader>
            <CardContent className="h-[400px] p-4">
              {dadosGrafico.some(d => d.receitas > 0 || d.despesas > 0) ? (
                <ChartContainer
                  config={{
                    receitas: { label: "Receitas", color: "hsl(var(--color-receitas))" },
                    despesas: { label: "Despesas", color: "hsl(var(--color-despesas))" },
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dadosGrafico}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="mes" />
                      <YAxis 
                        tickFormatter={(value) => 
                          new Intl.NumberFormat('pt-BR', {
                            notation: 'compact',
                            compactDisplay: 'short',
                            style: 'currency',
                            currency: 'BRL',
                          }).format(value)
                        }
                      />
                      <ChartTooltip 
                        content={
                          <ChartTooltipContent 
                            formatter={(value) => 
                              new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(value as number)
                            }
                          />
                        }
                      />
                      <Legend />
                      <Bar dataKey="receitas" fill="hsl(var(--color-receitas))" name="Receitas" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="despesas" fill="hsl(var(--color-despesas))" name="Despesas" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-financeiro-text-light">
                    <BarChart3 className="h-16 w-16 mx-auto mb-3 opacity-30" />
                    <p className="mb-2">Nenhum dado disponível para exibição</p>
                    <p className="text-sm mb-6">Adicione transações para visualizar seus dados financeiros</p>
                    <Button className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark">
                      Adicionar transação
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="proximas-transacoes" className="mt-6">
          <Card className="financeiro-card">
            <CardHeader>
              <CardTitle className="text-financeiro-text-dark">Próximas Transações</CardTitle>
              <CardDescription className="text-financeiro-text-light">Transações programadas para os próximos 30 dias</CardDescription>
            </CardHeader>
            <CardContent>
              {transacoesFuturas.length === 0 ? (
                <div className="py-10 text-center text-financeiro-text-light">
                  <CalendarIcon className="h-16 w-16 mx-auto mb-3 opacity-30" />
                  <p className="mb-2">Nenhuma transação programada para os próximos 30 dias</p>
                  <Button className="mt-4 bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark">
                    Agendar transação
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {transacoesFuturas.map((transacao) => (
                    <div
                      key={transacao.id}
                      className="flex justify-between items-center p-4 rounded-lg border border-financeiro-border-light"
                    >
                      <div className="flex items-center">
                        <div className={`w-2 h-10 rounded-full mr-4 ${
                          transacao.tipo === "receita" ? "bg-financeiro-accent-DEFAULT" : "bg-financeiro-text-DEFAULT"
                        }`} />
                        <div>
                          <h3 className="font-medium text-financeiro-text-DEFAULT">{transacao.descricao}</h3>
                          <div className="flex items-center text-sm text-financeiro-text-light">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {format(new Date(transacao.data), "dd 'de' MMMM", { locale: pt })}
                            {transacao.recorrente && (
                              <span className="ml-2 text-xs financeiro-badge-neutral">
                                {transacao.periodicidade}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`font-medium ${
                          transacao.tipo === "receita" ? "text-financeiro-accent-DEFAULT" : "text-financeiro-text-DEFAULT"
                        }`}>
                          {transacao.tipo === "receita" ? "+" : "-"}
                          {transacao.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                        <div className="text-xs text-financeiro-text-light mt-1">
                          {transacao.categoria}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DashboardFinanceiro;
